import { HttpErrorResponse } from '@angular/common/http';
import { Action } from '@ngrx/store';
import { catchError, Observable, of, OperatorFunction } from 'rxjs';
import { getErrorMessage } from '../../../models/error.model';
import { IToastie } from '../models/toastie.model';

/**
 * Extracts a human-friendly message from various error shapes (HttpErrorResponse-first).
 * @param error - The error to build the message from.
 * @param overrideMessage - An optional message to override the default message.
 * @returns A human-friendly message - "[status-code]: detail".
 */
function buildErrorToastMessage(error: unknown, overrideMessage?: string): string {
  const httpError = error as HttpErrorResponse;
  const statusLabel = httpError?.status != null ? getErrorMessage(httpError) : 'Error';
  const detail: string =
    overrideMessage ??
    httpError?.error?.error?.message ??
    (httpError?.error as { message?: string })?.message ??
    httpError?.message ??
    'Something went wrong';

  return `[${statusLabel}]: ${detail}`;
}

/**
 * Creates a catchError operator that converts errors into failure actions with toasts.
 * Use inside pipe() just like regular catchError.
 *
 * Example:
 *   catchErrorWithToast((error) => loadFailure({ error }))
 *   catchErrorWithToast((error) => loadFailure({ error }), 'Custom message')
 */
export function catchErrorWithToast<TInput, TAction extends Action>(
  buildFailureAction: (error: unknown) => TAction,
  toastMessage?: string
): OperatorFunction<TInput, TInput | (TAction & { toast: IToastie })> {
  return catchError<TInput, Observable<TAction & { toast: IToastie }>>((error) => {
    const failure = buildFailureAction(error);
    const toast: IToastie = {
      message: buildErrorToastMessage(error, toastMessage),
      toastType: 'error',
      showNotification: true,
    };
    return of({ ...failure, toast });
  });
}
